import React from 'react';
import ReactDOM from 'react-dom';
import Wahanda from 'common/wahanda';
import App from 'common/backbone-app';
import { assoc } from 'mori';
import { appState } from 'state';
import { enableRecording } from 'common/analytics';
import { Provider } from 'react-redux';
import storeBuilder from 'reduxStore/store';
import { parseTransactionReportDateFromWindowHash } from 'src/url-states/reports-transactions';
import { initialStatePopulation } from 'components/dashboard/actions';
import { canViewSelfEmploymentReports } from 'components/reports/SelfEmploymentReports/canViewSelfEmploymentReports';
import { Reports } from './container';

const DATE_KEY = 'reports/transactions/date';

// Read the date from the hash first, or else return the venue time zone date.
const getInitialDate: () => string = () => {
  const fromHash = parseTransactionReportDateFromWindowHash();
  if (fromHash) {
    return fromHash;
  }
  return Wahanda.Date.toApiString(Wahanda.Date.createVenueDate());
};

function updateStateWithExternalData() {
  // this is only needed for transaction reports
  // move this to the transaction reports view.
  appState.swap((oldState) =>
    assoc(oldState, DATE_KEY, getInitialDate(), 'venueId', App.getVenueId()),
  );
}

export const ReportsInitializer = (params) => {
  enableRecording();
  const store = storeBuilder();
  const E = Wahanda.Event;
  const reportsElement = params.containerElement as HTMLElement | undefined;
  const appointmentOpenEvents = [E.APPOINTMENT_OPENED, E.BOOKING_OPENED].join(' ');
  const appointmentCloseEvents = [E.APPOINTMENT_CLOSED, E.BOOKING_CLOSED].join(' ');
  let isDisposed = false;

  const onVenueChanged = () => {
    if (!isDisposed) {
      instance.render();
    }
  };
  const onVenueChanging = () => {
    if (!isDisposed) {
      ReactDOM.unmountComponentAtNode(params.tabElement);
    }
  };
  const onAppointmentOpened = () => {
    reportsElement?.classList.add('printable-hide');
  };
  const onAppointmentClosed = () => {
    reportsElement?.classList.remove('printable-hide');
  };

  const instance = {
    destroy() {
      if (isDisposed) {
        return;
      }
      isDisposed = true;
      App.off(E.APP_VENUE_CHANGED, onVenueChanged)
        .off(E.APP_VENUE_IS_CHANGING, onVenueChanging)
        .off(appointmentOpenEvents, onAppointmentOpened)
        .off(appointmentCloseEvents, onAppointmentClosed);
      ReactDOM.unmountComponentAtNode(params.tabElement);
    },
    render() {
      App.headerView.setModule('reports');
      updateStateWithExternalData();
      const viewTransactionsTab = Wahanda.Permissions.canViewTransactionReport();
      const canViewBookings = !App.isTaxAuditor() && Wahanda.Permissions.viewBookings();
      const supplierId = App.getSupplierId();
      const pointOfSaleEnabled = App.config.get('venue').pointOfSaleEnabled;
      store.dispatch(
        initialStatePopulation({
          supplierId,
          pointOfSaleEnabled,
        }),
      );
      const props = {
        canViewClientReports: Wahanda.Permissions.canViewClientReports(),
        canViewSaleReports: Wahanda.Permissions.viewReportsSales(),
        canViewBookingsReport: canViewBookings,
        canViewTransactionsReport: viewTransactionsTab,
        canViewOtherReports: canViewBookings || viewTransactionsTab,
        canViewPosReports: Wahanda.Permissions.viewPosReports(),
        canViewDAC7Reports: Wahanda.Permissions.viewDAC7Reports(),
        canViewSelfEmploymentReports: canViewSelfEmploymentReports(),
        venueId: App.getVenueId(),
        supplierId,
        pointOfSaleEnabled,
      };
      ReactDOM.render(
        <Provider store={store}>
          <Reports {...props} />
        </Provider>,
        params.tabElement,
      );
    },
  };

  App.on(E.APP_VENUE_CHANGED, onVenueChanged)
    .on(E.APP_VENUE_IS_CHANGING, onVenueChanging)
    .on(appointmentOpenEvents, onAppointmentOpened)
    .on(appointmentCloseEvents, onAppointmentClosed);

  return instance;
};
